import { PersistenceState, Image } from "./common";
import { TagCharger } from "./tag";

export type ChargerModel = {
  locationId: string;
  tenantId: string;
  name: string;
  manufacturer: string;
  brand: string;
  textInstructionForNavigation: string;
  textInstructionHowToStartCharging: string;
  serviceNote: string;
  externalId: string;
  villaProIntegration: boolean;
  lastActivityDate: string;
  loadBalancerId: string | null;
  manualFlexiCharging: boolean;
  qVendTid: string | null;
};

export type ConfigItem = {
  key: string;
  value: string;
  readonly?: boolean;
};

export type Charger = ChargerModel & {
  id: string;
  connectors: ChargerConnector[];
  images: Image[];
  tags: TagCharger[];
};

export enum ChargerConnectorType {
  Ac,
  Dc,
}

export enum ChargerConnectorSocketType {
  Type1,
  Type2,
  CHAdeMO,
  CCS,
}

export enum ChargerConnectorState {
  Free,
  Busy,
  Error,
  Unknown,
}

export enum ChargerConnectorAvailability {
  Available,
  Unavailable,
}

export type ChargerConnectorModel = {
  chargerId: string;
  type: ChargerConnectorType;
  socketType: ChargerConnectorSocketType;
  state: ChargerConnectorState;
  externalId: number;
  availability: ChargerConnectorAvailability;
  ocppStatus: string;
  oicpStatus: number;
  evseId: string;
};

export type ChargerConnector = ChargerConnectorModel & {
  id: string;
  persistenceState: PersistenceState;
};

export type ChargerConnectorSpecification = {
  type: ChargerConnectorType;
  socketType: ChargerConnectorSocketType;
};

function getChargerConnectorsSpecifications(): ChargerConnectorSpecification[] {
  return [
    {
      type: ChargerConnectorType.Ac,
      socketType: ChargerConnectorSocketType.Type1,
    },
    {
      type: ChargerConnectorType.Ac,
      socketType: ChargerConnectorSocketType.Type2,
    },
    {
      type: ChargerConnectorType.Dc,
      socketType: ChargerConnectorSocketType.CCS,
    },
    {
      type: ChargerConnectorType.Dc,
      socketType: ChargerConnectorSocketType.CHAdeMO,
    },
  ];
}

export function getSpecificationForSocketType(
  socketType: ChargerConnectorSocketType,
): ChargerConnectorSpecification | undefined {
  return getChargerConnectorsSpecifications().find(
    (x) => x.socketType === socketType,
  );
}

export function getChargerConnectorTypes(t: any) {
  return [
    {
      id: ChargerConnectorType.Ac,
      label: t("locations.connectorTypeAc"),
    },
    {
      id: ChargerConnectorType.Dc,
      label: t("locations.connectorTypeDc"),
    },
  ];
}
export function getLabelForChargerConnectorType(
  type: ChargerConnectorType | null | undefined,
  t: any,
) {
  if (type === null || type === undefined) {
    return t("common.unknown");
  }

  const foundType = getChargerConnectorTypes(t).find((x) => x.id === type);
  return foundType ? foundType.label : t("common.unknown");
}

const getConncetorStateLabel = (t: any, ocppStatus: string | undefined) => {
  if (ocppStatus === undefined) {
    return "";
  }

  var label = "";
  switch (ocppStatus) {
    case "Charging":
    case "SuspendedEV":
    case "SuspendedEVSE":
    case "Reserved":
    case "Finishing":
    case "Unvailable":
    case "Faulted":
      label = t("locations.connectorBusy");
      break;
    case "Available":
      label = t("locations.connectorAvailable");
      break;
    case "Preparing":
      label = t("locations.connectorPreparing");
      break;
    default:
      label = "";
  }
  return label;
};

export function getChargerConnectorSocketTypes(t: any) {
  return [
    {
      id: ChargerConnectorSocketType.Type1,
      label: t("locations.connectorSocketTypeType1"),
    },
    {
      id: ChargerConnectorSocketType.Type2,
      label: t("locations.connectorSocketTypeType2"),
    },
    {
      id: ChargerConnectorSocketType.CHAdeMO,
      label: t("locations.connectorSocketTypeChademo"),
    },
    {
      id: ChargerConnectorSocketType.CCS,
      label: t("locations.connectorSocketTypeCcs"),
    },
  ];
}
export function getLabelForChargerConnectorSocketType(
  socketType: ChargerConnectorSocketType,
  t: any,
) {
  const foundSocketType = getChargerConnectorSocketTypes(t).find(
    (x) => x.id === socketType,
  );
  return foundSocketType ? foundSocketType.label : t("common.unknown");
}

export const getConnectorLabel = (
  t: any,
  socketType: ChargerConnectorSocketType,
  connector?: ChargerConnector,
) => {
  const socketLabel = getLabelForChargerConnectorSocketType(socketType, t);
  const specification = getSpecificationForSocketType(socketType);
  const ocppStatus = connector ? connector.ocppStatus : undefined;
  const externalId = connector ? connector.externalId : undefined;
  let statusLabel = "";

  // Ak je OCPP status 'Preparing', vynútime label pre Preparing stav
  if (ocppStatus === "Preparing") {
    statusLabel = getConncetorStateLabel(t, ocppStatus);
  }
  // Inak použijeme štandardný label podľa číselného stavu (Free/Busy/Error)
  else if (ocppStatus !== undefined) {
    statusLabel = getConncetorStateLabel(t, ocppStatus);
  }

  if (specification) {
    const typeLabel = getLabelForChargerConnectorType(specification.type, t);
    let result = `${socketLabel} (${typeLabel})`;

    // Formátovanie výsledného reťazca
    if (externalId !== undefined) {
      result = `${externalId} - ${result} ${statusLabel ? statusLabel : ""}`;
    }
    return result;
  }

  return t("common.unknown");
};
